<template>
  <md-dialog
    :md-active.sync="isDialogVisible"
    :md-click-outside-to-close="false"
    @md-opened="dialogOpened"
  >
    <md-dialog-title>{{ user.uid ? 'Update' : 'New' }} User</md-dialog-title>
    <form id="this.form_id" @submit.prevent="saveUser" v-if="!isSuperAdmin || organisations">
      <md-dialog-content>
        <md-field v-if="!user.isCurrentUser" :class="getValidationClass('email')">
          <label for="email">Email</label>
          <md-input v-model="user.email" ref="email" />
          <span class="md-error" v-if="!$v.user.email.required">The email is required</span>
          <span class="md-error" v-else-if="!$v.user.email.email">Invalid email</span>
        </md-field>

        <md-field v-if="!user.isCurrentUser" :class="getValidationClass('password')">
          <label>Password</label>
          <md-input v-model="user.password" />
          <span class="md-error" v-if="!$v.user.password.required">Password is required</span>
          <span
            class="md-error"
            v-else-if="!$v.user.password.minLength"
          >Password must have at least {{$v.user.password.$params.minLength.min}} letters.</span>
        </md-field>

        <md-field v-if="isSuperAdmin && !user.isCurrentUser" :class="getValidationClass('orgId')">
          <label>Organisation</label>
          <md-select v-model="user.orgId" name="orgId">
            <md-option
              v-for="organisation in organisations"
              :key="organisation.id"
              :value="organisation.id"
            >{{ organisation.name }}</md-option>
          </md-select>
          <span class="md-error" v-if="!$v.user.orgId.required">Organisation is required</span>
        </md-field>

        <md-field>
          <label>Display Name</label>
          <md-input v-model="user.displayName" ref="displayName" />
        </md-field>

        <md-field :class="getValidationClass('photoURL')">
          <label>Photo URL</label>
          <md-input v-model="user.photoURL" />
          <span class="md-error" v-if="!$v.user.photoURL.url">Photo URL must be a valid URL</span>
        </md-field>

        <div class="block" v-if="user.isCurrentUser">
            <div class="title">Do you want to allow analytics to be collected while using EarthBlox?</div>
            <div class="input">
                <md-switch v-model="analyticsAgreed">Yes</md-switch>
            </div>
        </div>

        
        <md-field v-if="!user.isCurrentUser">
          <label for="movies">Roles</label>
          <md-select v-model="user.roles" name="roles" multiple>
            <md-option value="admin">admin</md-option>
            <md-option v-if="isSuperAdmin" value="superadmin">superadmin</md-option>
          </md-select>
        </md-field>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-raised" id="delete-button" @click="deleteUser" v-if="isEdit">Delete User</md-button>
        <md-button class="md-primary" @click="closeDialog">Close</md-button>
        <md-button type="submit" class="md-primary">Save</md-button>
      </md-dialog-actions>
    </form>
  </md-dialog>
</template>

<script>
/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 * 
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 * 
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
*/
import { validationMixin } from "vuelidate";
import {
  required,
  email,
  minLength,
  url,
  requiredUnless
} from "vuelidate/lib/validators";
import { organisationsCollection } from "../firebase.js";
import { AuthService } from "../services/auth.service";

function getDefaultUser() {
  return {
    email: "",
    password: "",
    displayName: "",
    photoURL: "",
    orgId: null,
    roles: [],
    termsConsent: null,
    termsVersion: "",
    analyticsAgreed: null
  };
}

export default {
  name: "UserListAddEditUser",
  mixins: [validationMixin],
  props: {
    isEdit: {
      type: Boolean,
      required: true
    },
    analyticsAgreement: {
        type: Boolean
    }
  },
  data: () => ({
    organisations: null,
    authenticatedUser: null,
    user: getDefaultUser(),
    isDialogVisible: false,
    analyticsAgreed: null
  }),
  validations: {
    user: {
      email: {
        required: requiredUnless(user => {
            return user.isLtiUser === true;
        }),
        email
      },
      password: {
        required: requiredUnless(user => {
          return user.uid != null;
        }),
        minLength: minLength(6)
      },
      photoURL: {
        url
      },
      orgId: {
        required
      }
    }
  },
  methods: {
    closeDialog() {
        this.isDialogVisible = false;

        // reset analytics agreement to the prop value
        this.analyticsAgreed = this.analyticsAgreement;
    },
    showDialog(user) {
      if (user) {
        this.user = Object.assign({}, user);
      } else {
        this.user = getDefaultUser();
        if (!this.isSuperAdmin && this.authenticatedUser) {
          this.user.orgId = this.authenticatedUser.orgId;
        }
      }
      this.isDialogVisible = true;
    },
    dialogOpened() {
      this.$v.$reset();

      setTimeout(() => {
        if (this.user.isCurrentUser) {
          this.$refs.displayName.$el.focus();
        } else {
          this.$refs.email.$el.focus();
        }        
      }, 200);
    },
    getValidationClass(fieldName) {
      const field = this.$v.user[fieldName];

      if (field) {
        return {
          "md-invalid": field.$invalid && field.$dirty
        };
      }
    },
    saveUser() {
        this.$v.$touch();

        if (this.$v.$invalid) {
            return;
        }

        if (this.user.isCurrentUser) {
            // save updated analytics agreement value to authenticated user
            this.authenticatedUser.analyticsAgreed = this.analyticsAgreed;
            // save updated analytics agreement value to emitted user object
            this.user.analyticsAgreed = this.analyticsAgreed;
        }

        this.$emit("user-save", this.user);

        this.isDialogVisible = false;
    },
    deleteUser(){
      if(confirm("Are you sure you want to delete this user, this is an irreversible action?")){
        this.$emit("user-delete", this.user)
        this.isDialogVisible = false;
      }    
    }
  },
  computed: {
    isUpdate() {
      return this.user.uid != null;
    },
    isSuperAdmin() {
      return this.authenticatedUser.roles && this.authenticatedUser.roles.includes("superadmin");
    }
  },
  created: function() {
    this.subscription = AuthService.loggedUser$.subscribe(user => {
      this.authenticatedUser = user;

      this.analyticsAgreed = this.analyticsAgreement;

      let isSuperAdmin = user.roles && user.roles.includes("superadmin");

      if (isSuperAdmin && !this.organisations) {
        organisationsCollection.get().then(querySnapshot => {
          let organisations = [];
          querySnapshot.forEach(doc => {
            let docData = doc.data();
            organisations.push({
              id: doc.id,
              name: docData.name
            });
          });
          this.organisations = organisations;
        });
      }
    });
  },
  beforeDestroy() {
    this.subscription.unsubscribe();
  }
};
</script>

<style scoped lang="scss">
.md-dialog-title {
  margin-bottom: 0;
}

#delete-button {
  background-color: #ff5252;
  color: #ffffff;
  margin-right: auto;
}
</style>
